export type DynamicResourceIdentifier = string

export type ResourceRef =
  | 'analytics.feed'
  | 'analytics.leads'
  | 'analytics.last-received'
  | 'analytics.flow-executions'
  | 'analytics.split-tests'
  | 'analytics.visitors'
  | 'righty.idea-well'
  | 'righty.ai-context'
  | 'righty.sitewide-cta-research'
  | `righty.visitor-insights.${DynamicResourceIdentifier}`
  | `integration.health.${DynamicResourceIdentifier}`
  | `export.status.${DynamicResourceIdentifier}`
  | `perfect-page.status.${DynamicResourceIdentifier}`
  | `apps.sync.${DynamicResourceIdentifier}`

export type ResourceInvalidation = {
  ref: ResourceRef
  revision: number | null
  deliveryId: string | null
  occurredAt: string | null
  source: 'event' | 'subscription'
}

export type OperationStatus = 'pending' | 'running' | 'succeeded' | 'failed' | 'expired'

export type OperationInvalidation = {
  operationId: string
  deliveryId: string | null
  status: OperationStatus | null
  progress: number | null
  source: 'event' | 'subscription'
}

export type ResourceObserver = (invalidation: ResourceInvalidation) => void
export type OperationObserver = (invalidation: OperationInvalidation) => void

type ResourceChangeWire = {
  deliveryId: string
  occurredAt: string
  resources: {
    ref: ResourceRef
    revision: number
  }[]
}

type OperationChangeWire = {
  deliveryId: string
  operationId: string
  status: OperationStatus
  progress?: number
}

type ResourceRegistration = {
  callback: ResourceObserver
}

type OperationRegistration = {
  callback: OperationObserver
}

const staticResourceRefs: Record<string, true> = {
  'analytics.feed': true,
  'analytics.leads': true,
  'analytics.last-received': true,
  'analytics.flow-executions': true,
  'analytics.split-tests': true,
  'analytics.visitors': true,
  'righty.idea-well': true,
  'righty.ai-context': true,
  'righty.sitewide-cta-research': true
}

const operationStatuses: Record<OperationStatus, true> = {
  pending: true,
  running: true,
  succeeded: true,
  failed: true,
  expired: true
}

const dynamicResourceRefPattern =
  /^(?:righty\.visitor-insights|integration\.health|export\.status|perfect-page\.status|apps\.sync)\.[A-Za-z0-9][A-Za-z0-9_-]{0,127}$/
const operationIdPattern = /^[A-Za-z0-9][A-Za-z0-9_-]{0,127}$/
const maxRememberedOperationDeliveries = 128

let activeTeamPid: string | null = null
let resourceObservers = new Map<ResourceRef, Set<ResourceRegistration>>()
let operationObservers = new Map<string, Set<OperationRegistration>>()
let knownRevisions = new Map<ResourceRef, number>()
let pendingResources = new Map<ResourceRef, ResourceInvalidation>()
let resourceFlushScheduled = false
let resourceChannelStarted = false
let resourceChannelSubscribed = false
const startedOperationChannels = new Set<string>()
const subscribedOperationChannels = new Set<string>()
const seenOperationDeliveries = new Set<string>()

export function parseResourceRef(value: unknown): ResourceRef | null {
  if (typeof value !== 'string') {
    return null
  }

  return staticResourceRefs[value] || dynamicResourceRefPattern.test(value) ? (value as ResourceRef) : null
}

function parseResourceChange(value: unknown): ResourceChangeWire | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return null
  }

  const payload = value as {
    deliveryId?: unknown
    occurredAt?: unknown
    resources?: unknown
  }

  if (
    typeof payload.deliveryId !== 'string' ||
    typeof payload.occurredAt !== 'string' ||
    !Array.isArray(payload.resources)
  ) {
    return null
  }

  const resources: ResourceChangeWire['resources'] = []
  for (const resource of payload.resources) {
    if (typeof resource !== 'object' || resource === null || Array.isArray(resource)) {
      return null
    }

    const item = resource as { ref?: unknown; revision?: unknown }
    const ref = parseResourceRef(item.ref)
    if (!ref || typeof item.revision !== 'number' || !Number.isSafeInteger(item.revision) || item.revision < 1) {
      return null
    }

    resources.push({ ref, revision: item.revision })
  }

  return { deliveryId: payload.deliveryId, occurredAt: payload.occurredAt, resources }
}

function parseOperationChange(value: unknown): OperationChangeWire | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return null
  }

  const payload = value as {
    deliveryId?: unknown
    operationId?: unknown
    status?: unknown
    progress?: unknown
  }

  if (
    typeof payload.deliveryId !== 'string' ||
    typeof payload.operationId !== 'string' ||
    typeof payload.status !== 'string'
  ) {
    return null
  }

  if (!operationIdPattern.test(payload.operationId) || !operationStatuses[payload.status as OperationStatus]) {
    return null
  }

  if (
    payload.progress !== undefined &&
    (typeof payload.progress !== 'number' ||
      !Number.isSafeInteger(payload.progress) ||
      payload.progress < 0 ||
      payload.progress > 100)
  ) {
    return null
  }

  return {
    deliveryId: payload.deliveryId,
    operationId: payload.operationId,
    status: payload.status as OperationStatus,
    ...(payload.progress === undefined ? {} : { progress: payload.progress })
  }
}

function flushPendingResources() {
  resourceFlushScheduled = false
  const pending = pendingResources
  pendingResources = new Map<ResourceRef, ResourceInvalidation>()

  for (const [ref, invalidation] of pending) {
    const registrations = resourceObservers.get(ref)
    if (!registrations) {
      continue
    }

    for (const registration of registrations) {
      registration.callback(invalidation)
    }
  }
}

function queueResourceInvalidation(invalidation: ResourceInvalidation) {
  pendingResources.set(invalidation.ref, invalidation)

  if (resourceFlushScheduled) {
    return
  }

  resourceFlushScheduled = true
  queueMicrotask(flushPendingResources)
}

function handleResourceChange(value: unknown) {
  const change = parseResourceChange(value)
  if (!change) {
    return
  }

  for (const resource of change.resources) {
    const knownRevision = knownRevisions.get(resource.ref)
    if (knownRevision !== undefined && resource.revision <= knownRevision) {
      continue
    }

    knownRevisions.set(resource.ref, resource.revision)
    queueResourceInvalidation({
      ref: resource.ref,
      revision: resource.revision,
      deliveryId: change.deliveryId,
      occurredAt: change.occurredAt,
      source: 'event'
    })
  }
}

function rememberOperationDelivery(deliveryId: string): boolean {
  if (seenOperationDeliveries.has(deliveryId)) {
    return false
  }

  seenOperationDeliveries.add(deliveryId)
  if (seenOperationDeliveries.size > maxRememberedOperationDeliveries) {
    seenOperationDeliveries.delete(seenOperationDeliveries.values().next().value as string)
  }

  return true
}

function handleOperationChange(value: unknown) {
  const change = parseOperationChange(value)
  if (!change || !rememberOperationDelivery(change.deliveryId)) {
    return
  }

  const registrations = operationObservers.get(change.operationId)
  if (!registrations) {
    return
  }

  const invalidation: OperationInvalidation = {
    operationId: change.operationId,
    deliveryId: change.deliveryId,
    status: change.status,
    progress: change.progress ?? null,
    source: 'event'
  }

  for (const registration of registrations) {
    registration.callback(invalidation)
  }
}

function reconcileResourceObservers() {
  for (const [ref, registrations] of resourceObservers) {
    const invalidation: ResourceInvalidation = {
      ref,
      revision: knownRevisions.get(ref) ?? null,
      deliveryId: null,
      occurredAt: null,
      source: 'subscription'
    }

    for (const registration of registrations) {
      registration.callback(invalidation)
    }
  }
}

function reconcileOperationObservers(operationId: string) {
  const registrations = operationObservers.get(operationId)
  if (!registrations) {
    return
  }

  const invalidation: OperationInvalidation = {
    operationId,
    deliveryId: null,
    status: null,
    progress: null,
    source: 'subscription'
  }

  for (const registration of registrations) {
    registration.callback(invalidation)
  }
}

function reconcileResourceRegistration(ref: ResourceRef, registration: ResourceRegistration) {
  if (!resourceObservers.get(ref)?.has(registration)) {
    return
  }

  registration.callback({
    ref,
    revision: knownRevisions.get(ref) ?? null,
    deliveryId: null,
    occurredAt: null,
    source: 'subscription'
  })
}

function reconcileOperationRegistration(operationId: string, registration: OperationRegistration) {
  if (!operationObservers.get(operationId)?.has(registration)) {
    return
  }

  registration.callback({
    operationId,
    deliveryId: null,
    status: null,
    progress: null,
    source: 'subscription'
  })
}

function leaveActiveTeamChannel() {
  if (activeTeamPid !== null) {
    window.Echo?.leave(`team-resources.${activeTeamPid}`)
  }
}

function switchTeam(teamPid: string) {
  if (activeTeamPid === null || activeTeamPid === teamPid) {
    activeTeamPid = teamPid
    return
  }

  leaveActiveTeamChannel()
  activeTeamPid = teamPid
  resourceObservers = new Map<ResourceRef, Set<ResourceRegistration>>()
  knownRevisions = new Map<ResourceRef, number>()
  pendingResources = new Map<ResourceRef, ResourceInvalidation>()
  resourceChannelStarted = false
  resourceChannelSubscribed = false
}

function removeResourceObserver(teamPid: string, ref: ResourceRef, registration: ResourceRegistration) {
  if (activeTeamPid !== teamPid) {
    return
  }

  const registrations = resourceObservers.get(ref)
  registrations?.delete(registration)
  if (registrations?.size === 0) {
    resourceObservers.delete(ref)
  }

  if (resourceObservers.size === 0) {
    leaveActiveTeamChannel()
    activeTeamPid = null
    knownRevisions = new Map<ResourceRef, number>()
    pendingResources = new Map<ResourceRef, ResourceInvalidation>()
    resourceChannelStarted = false
    resourceChannelSubscribed = false
  }
}

function removeOperationObserver(operationId: string, registration: OperationRegistration) {
  const registrations = operationObservers.get(operationId)
  registrations?.delete(registration)
  if (registrations?.size === 0) {
    operationObservers.delete(operationId)
    startedOperationChannels.delete(operationId)
    subscribedOperationChannels.delete(operationId)
    window.Echo?.leave(`operations.${operationId}`)
  }
}

export function observeResource(
  teamPid: string,
  ref: ResourceRef,
  callback: ResourceObserver,
  signal: AbortSignal
): () => void {
  const parsedRef = parseResourceRef(ref)
  if (!parsedRef || signal.aborted || !window.Echo) {
    return () => {}
  }

  switchTeam(teamPid)
  let registrations = resourceObservers.get(parsedRef)
  if (!registrations) {
    registrations = new Set<ResourceRegistration>()
    resourceObservers.set(parsedRef, registrations)
  }

  const registration = { callback }
  registrations.add(registration)

  if (!resourceChannelStarted) {
    resourceChannelStarted = true
    window.Echo.private(`team-resources.${teamPid}`)
      .listen('ResourceChanged', handleResourceChange)
      .subscribed(() => {
        if (activeTeamPid !== teamPid) {
          return
        }
        resourceChannelSubscribed = true
        reconcileResourceObservers()
      })
  } else if (resourceChannelSubscribed) {
    queueMicrotask(() => reconcileResourceRegistration(parsedRef, registration))
  }

  let removed = false
  const remove = () => {
    if (removed) {
      return
    }

    removed = true
    signal.removeEventListener('abort', remove)
    removeResourceObserver(teamPid, parsedRef, registration)
  }

  signal.addEventListener('abort', remove, { once: true })

  return remove
}

export function observeOperation(operationId: string, callback: OperationObserver, signal: AbortSignal): () => void {
  if (!operationIdPattern.test(operationId) || signal.aborted || !window.Echo) {
    return () => {}
  }

  let registrations = operationObservers.get(operationId)
  if (!registrations) {
    registrations = new Set<OperationRegistration>()
    operationObservers.set(operationId, registrations)
  }

  const registration = { callback }
  registrations.add(registration)

  if (!startedOperationChannels.has(operationId)) {
    startedOperationChannels.add(operationId)
    window.Echo.private(`operations.${operationId}`)
      .listen('OperationChanged', handleOperationChange)
      .subscribed(() => {
        subscribedOperationChannels.add(operationId)
        reconcileOperationObservers(operationId)
      })
  } else if (subscribedOperationChannels.has(operationId)) {
    queueMicrotask(() => reconcileOperationRegistration(operationId, registration))
  }

  let removed = false
  const remove = () => {
    if (removed) {
      return
    }

    removed = true
    signal.removeEventListener('abort', remove)
    removeOperationObserver(operationId, registration)
  }

  signal.addEventListener('abort', remove, { once: true })

  return remove
}
